import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { CategorySpend, VendorSpend, RequesterSpend } from '@/hooks/useSpendAnalytics';
import { format } from 'date-fns';

interface ExportButtonProps {
  startDate: Date;
  endDate: Date;
  categories: CategorySpend[] | undefined;
  vendors: VendorSpend[] | undefined;
  requesters: RequesterSpend[] | undefined;
  jobsData: Array<{
    jobId: string;
    publicId: string;
    title: string;
    category: string;
    vendorName: string;
    amount: number;
    completedDate: string;
    requesterName: string;
  }> | undefined;
  isLoading: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function escapeCSV(value: string | number): string {
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function ExportButton({
  startDate,
  endDate,
  categories,
  vendors,
  requesters,
  jobsData,
  isLoading,
}: ExportButtonProps) {
  const handleExport = () => {
    const lines: string[] = [];

    // Header
    lines.push('SPEND REPORT');
    lines.push(`Date Range: ${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`);
    lines.push(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`);
    lines.push('');

    // Summary
    const totalSpend = categories?.reduce((sum, c) => sum + c.spend, 0) || 0;
    const totalJobs = categories?.reduce((sum, c) => sum + c.jobCount, 0) || 0;
    lines.push('SUMMARY');
    lines.push(`Total Spend,${formatCurrency(totalSpend)}`);
    lines.push(`Total Jobs,${totalJobs}`);
    lines.push('');

    // Categories
    if (categories && categories.length > 0) {
      lines.push('SPEND BY CATEGORY');
      lines.push('Category,Spend,Jobs,Percentage');
      for (const cat of categories) {
        lines.push(
          `${escapeCSV(cat.label)},${formatCurrency(cat.spend)},${cat.jobCount},${cat.percentage.toFixed(1)}%`
        );
      }
      lines.push('');
    }

    // Vendors
    if (vendors && vendors.length > 0) {
      lines.push('SPEND BY VENDOR');
      lines.push('Vendor,Spend,Jobs');
      for (const vendor of vendors) {
        lines.push(
          `${escapeCSV(vendor.vendorName)},${formatCurrency(vendor.spend)},${vendor.jobCount}`
        );
      }
      lines.push('');
    }

    // Requesters
    if (requesters && requesters.length > 0) {
      lines.push('SPEND BY REQUESTER');
      lines.push('Requester,Jobs,Total Spend,Avg Job Value,Percentage');
      for (const req of requesters) {
        lines.push(
          `${escapeCSV(req.name)},${req.jobCount},${formatCurrency(req.totalSpend)},${formatCurrency(req.avgJobValue)},${req.percentage.toFixed(1)}%`
        );
      }
      lines.push('');
    }

    // Job Details
    if (jobsData && jobsData.length > 0) {
      lines.push('JOB DETAILS');
      lines.push('Job ID,Title,Category,Vendor,Amount,Date,Requester');
      for (const job of jobsData) {
        lines.push(
          `${escapeCSV(job.publicId)},${escapeCSV(job.title)},${escapeCSV(job.category)},${escapeCSV(job.vendorName)},${formatCurrency(job.amount)},${job.completedDate},${escapeCSV(job.requesterName)}`
        );
      }
    }

    // Create and download file
    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `spend-report-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Button onClick={handleExport} disabled={isLoading} variant="outline" size="sm">
      {isLoading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 mr-2" />
      )}
      Export to CSV
    </Button>
  );
}
